import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Download, RefreshCcw, Trash2 } from "lucide-react";
import { listReservations, updateReservation, deleteReservation, reservationsCsvUrl } from "@/lib/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["new", "confirmed", "contacted", "converted", "cancelled"];
const STATUS_COLORS = {
  new: "var(--gi-maroon)",
  confirmed: "#2A6F97",
  contacted: "var(--gi-gold)",
  converted: "var(--gi-green)",
  cancelled: "var(--gi-muted)",
};

export default function AdminReservations() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const canEdit = user?.role === "owner" || user?.role === "editor";
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listReservations({ search: search || undefined, status: status || undefined });
      setItems(rows);
      if (selected) {
        const fresh = rows.find(r => r.id === selected.id);
        if (fresh) setSelected(fresh);
      }
    } catch { toast.error("Unable to load reservations"); }
    finally { setLoading(false); }
  }, [search, status, selected]);

  useEffect(() => { load(); /* eslint-disable-line */ }, [status]);

  const doDownload = () => {
    const href = reservationsCsvUrl({ search: search || undefined, status: status || undefined });
    // Use a link so it inherits the auth cookie and triggers a download.
    window.location.href = href;
  };

  return (
    <div data-testid="admin-reservations-page">
      <PageHeader eyebrow="Reservations" title="Advance reservations" note="Non-binding print reservations from the public site. Update status here; no payment is processed." />

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[240px] flex gap-2">
          <input
            className="gi-input flex-1"
            placeholder="Search name / email / phone / reference / book"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === "Enter" && load()}
            data-testid="reservations-search-input"
          />
          <button onClick={load} className="btn-ghost" data-testid="reservations-search-btn"><Search size={12} /></button>
        </div>
        <select
          className="gi-input"
          style={{ width: 180 }}
          value={status}
          onChange={e => setStatus(e.target.value)}
          data-testid="reservations-status-filter"
        >
          <option value="">All statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={load} className="btn-ghost" title="Refresh" data-testid="reservations-refresh-btn"><RefreshCcw size={12} /></button>
        {canEdit && (
          <button onClick={doDownload} className="btn-outline" data-testid="reservations-export-btn"><Download size={12} /> Export CSV</button>
        )}
      </div>

      <div className="mt-6 border bg-white" style={{ borderColor: "var(--gi-border)" }}>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase" style={{ background: "var(--gi-ivory-2)", color: "var(--gi-muted)" }}>
            <tr>
              <th className="text-left px-4 py-3">Reference</th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Book</th>
              <th className="text-left px-4 py-3">Qty</th>
              <th className="text-left px-4 py-3">Country</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Created</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm" style={{ color: "var(--gi-muted)" }}>No reservations yet.</td></tr>
            )}
            {items.map(r => (
              <tr key={r.id} onClick={() => setSelected(r)} className="border-t cursor-pointer hover:bg-[var(--gi-ivory-2)]" style={{ borderColor: "var(--gi-divider)" }} data-testid={`reservations-row-${r.reference_number}`}>
                <td className="px-4 py-3 font-mono text-xs">{r.reference_number}</td>
                <td className="px-4 py-3">
                  <div>{r.full_name}</div>
                  <div className="text-xs" style={{ color: "var(--gi-muted)" }}>{r.email}</div>
                </td>
                <td className="px-4 py-3">
                  <div>{r.book_title_snapshot}</div>
                  {r.edition_language && <div className="text-xs" style={{ color: "var(--gi-muted)" }}>{r.edition_language}</div>}
                </td>
                <td className="px-4 py-3">{r.quantity}</td>
                <td className="px-4 py-3 text-xs">{[r.city, r.state, r.country].filter(Boolean).join(", ")}</td>
                <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-3 text-xs" style={{ color: "var(--gi-muted)" }}>{new Date(r.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <ReservationDrawer
          res={selected}
          isOwner={isOwner}
          canEdit={canEdit}
          onClose={() => setSelected(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

const StatusBadge = ({ status }) => (
  <span
    className="px-2 py-1 text-xs uppercase tracking-wide font-accent"
    style={{ background: `${STATUS_COLORS[status] || "#888"}15`, color: STATUS_COLORS[status] || "#333", border: `1px solid ${STATUS_COLORS[status] || "#888"}` }}
    data-testid={`reservation-status-${status}`}
  >
    {status}
  </span>
);

const ReservationDrawer = ({ res, isOwner, canEdit, onClose, onChanged }) => {
  const [status, setStatus] = useState(res.status);
  const [notes, setNotes] = useState(res.internal_notes || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setStatus(res.status); setNotes(res.internal_notes || ""); }, [res.id]);

  const save = async () => {
    setSaving(true);
    try {
      await updateReservation(res.id, { status, internal_notes: notes });
      toast.success("Reservation updated");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete reservation ${res.reference_number}? This cannot be undone from the UI.`)) return;
    try {
      await deleteReservation(res.id);
      toast.success("Reservation deleted");
      onClose();
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end" style={{ background: "rgba(24,20,18,0.5)" }} onClick={onClose} data-testid="reservation-drawer">
      <div className="w-full max-w-lg bg-white h-full overflow-y-auto p-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="font-accent text-[10px]" style={{ color: "var(--gi-gold)" }}>Reservation</div>
            <div className="mt-1 font-heading text-2xl" style={{ color: "var(--gi-maroon)" }} data-testid="drawer-reference">{res.reference_number}</div>
          </div>
          <button onClick={onClose} className="btn-ghost">Close</button>
        </div>

        <dl className="mt-6 grid grid-cols-3 gap-x-4 gap-y-3 text-sm">
          <Row label="Book">{res.book_title_snapshot}</Row>
          <Row label="Edition">{res.edition_language || "—"}</Row>
          <Row label="Quantity">{res.quantity}</Row>
          <Row label="Name">{res.full_name}</Row>
          <Row label="Email"><a href={`mailto:${res.email}`} className="underline">{res.email}</a></Row>
          <Row label="Phone"><a href={`tel:${res.phone.replace(/\s+/g, "")}`} className="underline">{res.phone}</a></Row>
          <Row label="City">{res.city}</Row>
          <Row label="State">{res.state || "—"}</Row>
          <Row label="Country">{res.country}</Row>
          <Row label="Submitted" wide>{new Date(res.created_at).toLocaleString()}</Row>
        </dl>

        {res.notes && (
          <div className="mt-5">
            <div className="gi-label">Customer note</div>
            <div className="text-sm p-3 border" style={{ borderColor: "var(--gi-border)", background: "var(--gi-ivory-2)" }}>{res.notes}</div>
          </div>
        )}

        <fieldset disabled={!canEdit} className="mt-6 space-y-4">
          <div>
            <label className="gi-label">Status</label>
            <select className="gi-input" value={status} onChange={e => setStatus(e.target.value)} data-testid="drawer-status-select">
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="gi-label">Internal notes (not visible to the customer)</label>
            <textarea rows={4} className="gi-input" value={notes} onChange={e => setNotes(e.target.value)} data-testid="drawer-notes" />
          </div>
        </fieldset>

        {canEdit && (
          <div className="mt-6 flex justify-between">
            <button onClick={save} disabled={saving} className="btn-maroon" data-testid="drawer-save-btn">{saving ? "Saving…" : "Save"}</button>
            {isOwner && <button onClick={remove} className="btn-ghost" data-testid="drawer-delete-btn"><Trash2 size={12} /> Delete</button>}
          </div>
        )}

        {res.status_history && res.status_history.length > 0 && (
          <div className="mt-8">
            <div className="gi-label">Status history</div>
            <ol className="space-y-2 text-xs">
              {res.status_history.map((h, i) => (
                <li key={i} className="flex items-baseline gap-2">
                  <StatusBadge status={h.status} />
                  <span>{new Date(h.changed_at).toLocaleString()}</span>
                  <span style={{ color: "var(--gi-muted)" }}>by {h.changed_by}</span>
                  {h.note && <span style={{ color: "var(--gi-muted)" }}>— {h.note}</span>}
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </div>
  );
};

const Row = ({ label, children, wide }) => (
  <div className={wide ? "col-span-3" : "col-span-1"}>
    <div className="gi-label">{label}</div>
    <div>{children}</div>
  </div>
);
